/**
 * **随机漫步** —— 一个"每帧挪一点点、但不会跑远"的数。
 *
 * 它原来是 `render/dying/layer.ts` 里给崩溃日志那一行行号写的一个内联 IIFE
 * （评审：「我之前为了其他目的已经写过一个 walk，将其标准化然后拿来用」）。
 * 现在抽出来给两处共用：
 *
 * - 濒死日志里那个**行号**（整数、0..192、初始 50）
 * - 故障滤镜的**强度**（连续、每帧随机漫步）
 *
 * ## 它和"随机数"的区别
 *
 * 每帧取一个独立随机数的话，画面是**噪点**（每一帧都跳到别处）；
 * 随机漫步是"接着上一帧挪一小步"，所以它读起来像一条**漂移的线**：
 * 一阵阵偏这边、一阵阵偏那边。故障要的就是后者 —— 信号在抖，不是屏幕在闪。
 *
 * ## 两条规矩
 *
 * 1. **步长是 `r² × maxStep`**（`r` 是 [0,1) 的随机数）：小步多、大步少，
 *    于是绝大多数帧只挪一点点。
 * 2. **方向往回偏**：离初始值越远，往回走的概率越大
 *    （`pNegative = 0.5 + 0.5 × 偏离比例`）。没有这一条的话它会一路漂到
 *    边界上贴着不动 —— 那就不再是漫步，而是"跑到头了"。
 *
 * ⚠️ 随机数发生器**必须由调用方给**（`core/rng` 的 `seededRandom`）——
 *    项目里所有"看起来是随机的画面"都走种子化 PRNG
 *    （见 `field.ts` / `glitch.ts` / `dying/layer.ts`），不然同一个种子
 *    给不出同一幅画面，"这个观感对不对"就没法断言。
 */

export interface WalkOptions {
  readonly min: number;
  readonly max: number;
  /** 起点。也是"往回偏"的那个中心 */
  readonly initial: number;
  /** 一步最多挪多远 */
  readonly maxStep: number;
  /**
   * 0..1 的随机数发生器。
   *
   * ⚠️ **必给**（项目里 `src/core/**` 不许用 `Math.random`，见
   *    `architecture.md` §4.1）。要复现就传 `core/rng` 的 `seededRandom`。
   */
  readonly random: () => number;
  /**
   * 取整方式。不给 = 连续值（故障强度要小数）；
   * 给 `Math.floor` = 整数（日志行号要整数）。
   */
  readonly snap?: (value: number) => number;
}

export interface Walk {
  /** 往前挪一步，返回**新值** */
  next(): number;
  /** 现在是多少（不推进） */
  readonly value: number;
  /** **回到起点。** 用在一段"情况重新开始"的地方（比如濒死结束、下次再来） */
  reset(): void;
}

export function createWalk(options: WalkOptions): Walk {
  const { min, max, initial, maxStep } = options;
  const random = options.random;
  const snap = options.snap ?? ((value: number): number => value);

  /**
   * ⚠️ 归一化用的是"从初始值到**更远**那一头的距离"，不是半幅：
   *    初始值不在正中间时（日志那个是 50/192），用半幅算的话
   *    `pNegative` 会在 0..1 之外，往回偏的力度就错了。
   */
  const maxRange = Math.max(max - initial, initial - min) || 1;
  let current = Math.max(min, Math.min(max, initial));

  return {
    next(): number {
      const offsetRatio = (current - initial) / maxRange;
      const pBackToward = 0.5 + 0.5 * offsetRatio;
      const magnitude = random() ** 2 * maxStep;
      const step = magnitude * (random() < pBackToward ? -1 : 1);
      current = snap(Math.max(min, Math.min(max, current + step)));
      return current;
    },
    reset(): void {
      current = Math.max(min, Math.min(max, initial));
    },
    get value(): number {
      return current;
    },
  };
}
